import { renderToString } from 'react-dom/server';

import i18n from 'i18n-react';
import type { ReactNode } from 'react';

import badges from 'assets/images/badges';

import { ContentHeader } from 'components/ContentHeader/ContentHeader';
import { getFancyboxButtonShareTpl } from 'components/FancyboxButtons/FancyboxButtonShare';
import SharingPopup from 'components/SharingPopup/SharingPopup';

import c from './_tierRewardsBBI.module.scss';

interface Tier {
  title: string;
  completedItems: number;
  count: number;
  copy: string;
}

export interface TierRewardsBBIProps {
  tiers: Tier[];
  tierIndex: number | null;
  totalProgressValue: number;
  loadedPointsHandler: boolean;
  mobile: boolean;
}

const TIER_CONFIG = [
  {
    pdf: 'https://front.travpromobile.com/940/2025%20-%20Travel+Nevada+-+BBI+-+Bronze+Status+Reward.pdf',
    titleKey: 'badgeView.bronzeRewardTitle',
    bodyKey: 'badgeView.bronzeReward',
  },
  {
    pdf: 'https://front.travpromobile.com/940/2025%20-%20Travel+Nevada+-+BBI+-+Silver+Status+Reward.pdf',
    titleKey: 'badgeView.silverRewardTitle',
    bodyKey: 'badgeView.silverReward',
  },
  {
    pdf: 'https://front.travpromobile.com/940/2025%20-%20Travel+Nevada+-+BBI+-+Gold+Status+Reward.pdf',
    titleKey: 'badgeView.goldRewardTitle',
    bodyKey: 'badgeView.goldReward',
  },
] as const;

const [bronzeConfig, silverConfig, goldConfig] = TIER_CONFIG;

const fancyOptions = (url: string, title: string) => {
  return {
    infobar: true,
    thumbs: { autoStart: true, axis: 'x' },
    baseClass: 'customBox',
    buttons: ['thumbs', 'share', 'close'],
    share: {
      tpl: renderToString(
        <SharingPopup
          url={url}
          title={title}
          image="https://imgtravpromobile.imgix.net/940/1711474783-1691516053-generalbackground.jpg?fit=clip&w=1500&h=auto"
        />,
      ),
    },
    btnTpl: { share: getFancyboxButtonShareTpl() },
    caption: '',
  };
};

interface RewardLinkProps {
  pdf: string;
  title: ReactNode;
  body: ReactNode;
  mobile: boolean;
}

function RewardLink({ pdf, title, body, mobile }: RewardLinkProps) {
  if (mobile) {
    return (
      <h1 className={c.rewardText}>
        {i18n.translate('badgeView.click')}
        <a
          onClick={() => {
            window.ReactNativeWebView?.postMessage(`pdf:${pdf}`);
          }}
        >
          {i18n.translate('badgeView.here')}
        </a>
        {body}
      </h1>
    );
  }

  return (
    <h1 className={c.rewardText}>
      {i18n.translate('badgeView.click')}
      <a href={pdf} data-fancybox data-options={JSON.stringify(fancyOptions(pdf, title as string))}>
        {i18n.translate('badgeView.here')}
      </a>
      {body}
    </h1>
  );
}

export function TierRewardsBBI({
  tiers,
  tierIndex,
  totalProgressValue,
  loadedPointsHandler,
  mobile,
}: TierRewardsBBIProps) {
  return (
    <>
      {tierIndex !== null && (
        <div className="badge-display__wrapper">
          <h1 className="badgeView__text">{tiers[tierIndex]?.copy}</h1>
        </div>
      )}
      {loadedPointsHandler && totalProgressValue >= 100 && (
        <div className={c.wrapper}>
          <hr />
          <ContentHeader header={i18n.translate('badgeView.tierRewards')} />
          {tiers.map((tier, index) => {
            if (tier.completedItems < tier.count) return null;
            const config = index === 0 ? bronzeConfig : index === 1 ? silverConfig : goldConfig;
            return (
              <div key={tier.title} className={c.reward}>
                <img className="badge" src={badges.tierBadges[index]} alt="Logo" />
                <RewardLink
                  pdf={config.pdf}
                  title={i18n.translate(config.titleKey)}
                  body={i18n.translate(config.bodyKey)}
                  mobile={mobile}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
